import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ResearchTab from '../components/ResearchTab';
import { TaskKanbanView, TaskListView } from '../components/GravityPlan';
import { ResearchEntityType } from '../services/researchService';
import { useOrganization } from '../context/OrganizationContext';
import { usePersistedPreference } from '../hooks/useFilterPersistence';
import { MasterPlanTask, masterPlanTaskService } from '../services/masterPlan';
import { okrService, OrganizationOKR } from '../services/okrs';
import { getAuthHeaders } from '../utils/auth';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  UserGroupIcon,
  ChartBarIcon,
  SparklesIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  BeakerIcon,
  UserPlusIcon,
  XMarkIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../constants/urls';

type AreaKPI = {
  name: string;
  description?: string;
  target?: any;
  current?: any;
  unit?: string;
  frequency?: string;
};

type AreaMember = {
  id: string;
  area_id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  role: string;
  joined_at: string;
};

type Area = {
  id: string;
  name: string;
  description?: string;
  value: string;
  color?: string;
  icon?: string;
  is_active: boolean;
  is_default: boolean;
  display_order: number;
  owner_id?: string;
  owner_name?: string;
  maturity_level?: 'emerging' | 'developing' | 'mature' | 'optimized';
  kpis?: AreaKPI[];
  created_at: string;
  updated_at: string;
  members?: AreaMember[];
  task_count?: number;
  okr_count?: number;
  research_count?: number;
};

const MATURITY_LEVELS = [
  {
    value: 'emerging',
    label: 'Emerging',
    color: 'bg-yellow-100 text-yellow-800',
  },
  {
    value: 'developing',
    label: 'Developing',
    color: 'bg-blue-100 text-blue-800',
  },
  { value: 'mature', label: 'Mature', color: 'bg-green-100 text-green-800' },
  {
    value: 'optimized',
    label: 'Optimized',
    color: 'bg-purple-100 text-purple-800',
  },
];

const MEMBER_ROLES = [
  { value: 'member', label: 'Member' },
  { value: 'lead', label: 'Lead' },
];

export default function AreaDetailTailwind() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedOrg: organization,
    userRole: userOrgRole,
    canEdit: userCanEdit,
  } = useOrganization();

  const [area, setArea] = useState<Area | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<Area>>({});
  const [tasks, setTasks] = useState<MasterPlanTask[]>([]);
  const [okrs, setOkrs] = useState<OrganizationOKR[]>([]);
  const [members, setMembers] = useState<AreaMember[]>([]);
  const [taskView, setTaskView] = usePersistedPreference<'list' | 'kanban'>(
    'areas.taskView',
    'kanban',
    'ui',
  );
  const [orgMembers, setOrgMembers] = useState<
    Array<{ user_id: string; user_name: string; user_email: string }>
  >([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('member');
  const [showAddKPI, setShowAddKPI] = useState(false);
  const [newKPI, setNewKPI] = useState<AreaKPI>({
    name: '',
    description: '',
    target: '',
    current: '',
    unit: '',
    frequency: '',
  });

  const getInitialTab = ():
    | 'overview'
    | 'team'
    | 'tasks'
    | 'okrs'
    | 'research' => {
    const hash = location.hash.replace('#', '') as any;
    const validTabs = ['overview', 'team', 'tasks', 'okrs', 'research'];
    if (validTabs.includes(hash)) {
      return hash;
    }
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState<
    'overview' | 'team' | 'tasks' | 'okrs' | 'research'
  >(getInitialTab());

  const handleTabChange = (
    tab: 'overview' | 'team' | 'tasks' | 'okrs' | 'research',
  ) => {
    setActiveTab(tab);
    navigate(`#${tab}`, { replace: true });
  };

  useEffect(() => {
    const hash = location.hash.replace('#', '') as any;
    const validTabs = ['overview', 'team', 'tasks', 'okrs', 'research'];
    if (validTabs.includes(hash)) {
      setActiveTab(hash);
    } else {
      setActiveTab('overview');
    }
  }, [location.hash]);

  useEffect(() => {
    if (organization && id) {
      fetchAreaDetail();
      fetchAreaMembers();
      fetchAreaTasks();
      fetchAreaOKRs();
      fetchOrgMembers();
    }
  }, [organization, id]);

  const fetchAreaDetail = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/areas/${id}?organization_id=${organization?.id}`,
        { headers: getAuthHeaders() },
      );
      setArea(response.data);
      setFormData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching area:', error);
      setLoading(false);
    }
  };

  const fetchAreaMembers = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/areas/${id}/members?organization_id=${organization?.id}`,
        { headers: getAuthHeaders() },
      );
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching area members:', error);
    }
  };

  const fetchAreaTasks = async () => {
    if (!organization || !id) return;
    try {
      const allTasks = await masterPlanTaskService.list(organization.id, {
        area_ids: [id],
      });
      setTasks(allTasks);
    } catch (error) {
      console.error('Error fetching area tasks:', error);
    }
  };

  const fetchAreaOKRs = async () => {
    if (!organization) return;
    try {
      const allOKRs = await okrService.listOKRs(organization.id);
      // Filter OKRs for this area
      const areaOKRs = allOKRs.filter(
        (okr: OrganizationOKR) => okr.area_id === id,
      );
      setOkrs(areaOKRs);
    } catch (error) {
      console.error('Error fetching area OKRs:', error);
    }
  };

  const fetchOrgMembers = async () => {
    if (!organization) return;
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organization.id}/members`,
        { headers: getAuthHeaders() },
      );
      setOrgMembers(response.data);

      // Fetch areas for task assignment
      const areasResponse = await axios.get(
        `${API_URL}/areas?organization_id=${organization.id}`,
        { headers: getAuthHeaders() },
      );
      setAreas(areasResponse.data || []);
    } catch (error) {
      console.error('Error fetching org members:', error);
    }
  };

  const handleSave = async () => {
    if (!organization || !area) return;

    try {
      const response = await axios.put(
        `${API_URL}/areas/${area.id}?organization_id=${organization.id}`,
        formData,
        { headers: getAuthHeaders() },
      );
      setArea(response.data);
      setEditMode(false);
    } catch (error) {
      console.error('Error updating area:', error);
      alert('Failed to update area');
    }
  };

  const handleAddMember = async () => {
    if (!organization || !selectedUserId) return;

    try {
      const response = await axios.post(
        `${API_URL}/areas/${id}/members?organization_id=${organization.id}`,
        {
          user_id: selectedUserId,
          role: selectedRole,
        },
        { headers: getAuthHeaders() },
      );
      setMembers([...members, response.data]);
      setShowAddMember(false);
      setSelectedUserId('');
      setSelectedRole('member');
    } catch (error) {
      console.error('Error adding member:', error);
      alert('Failed to add member');
    }
  };

  const handleUpdateMemberRole = async (userId: string, newRole: string) => {
    if (!organization) return;

    try {
      const response = await axios.put(
        `${API_URL}/areas/${id}/members/${userId}?organization_id=${organization.id}`,
        { role: newRole },
        { headers: getAuthHeaders() },
      );
      setMembers(
        members.map((m) => (m.user_id === userId ? response.data : m)),
      );
    } catch (error) {
      console.error('Error updating member role:', error);
      alert('Failed to update member role');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!organization) return;
    if (!window.confirm('Remove this member from the area?')) return;

    try {
      await axios.delete(
        `${API_URL}/areas/${id}/members/${userId}?organization_id=${organization.id}`,
        { headers: getAuthHeaders() },
      );
      setMembers(members.filter((m) => m.user_id !== userId));
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member');
    }
  };

  const handleAddKPI = () => {
    if (newKPI.name) {
      const updatedKPIs = [...(formData.kpis || []), newKPI];
      setFormData({ ...formData, kpis: updatedKPIs });
      setNewKPI({
        name: '',
        description: '',
        target: '',
        current: '',
        unit: '',
        frequency: '',
      });
      setShowAddKPI(false);
    }
  };

  const handleRemoveKPI = (index: number) => {
    const updatedKPIs = (formData.kpis || []).filter((_, i) => i !== index);
    setFormData({ ...formData, kpis: updatedKPIs });
  };

  const getMaturityBadge = (level?: string) => {
    const maturity = MATURITY_LEVELS.find((m) => m.value === level);
    if (!maturity) return null;
    return (
      <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${maturity.color}`}
      >
        {maturity.label}
      </span>
    );
  };

  const getAvailableMembers = () => {
    const currentMemberIds = members.map((m) => m.user_id);
    return orgMembers.filter((om) => !currentMemberIds.includes(om.user_id));
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-500">Loading area details...</div>
      </div>
    );
  }

  if (!area) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-gray-500">Area not found</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  onClick={() => navigate('/areas')}
                  className="mr-4 text-gray-400 hover:text-gray-600"
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>
                <div className="flex items-center">
                  {area.color && (
                    <div
                      className="w-10 h-10 rounded-full mr-3 flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: area.color }}
                    >
                      {area.name[0]}
                    </div>
                  )}
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {area.name}
                    </h1>
                    {area.description && (
                      <p className="text-sm text-gray-500 mt-1">
                        {area.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getMaturityBadge(area.maturity_level)}
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    area.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {area.is_active ? 'Active' : 'Inactive'}
                </span>
                {userCanEdit && !editMode && activeTab === 'overview' && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                )}
                {editMode && (
                  <>
                    <button
                      onClick={handleSave}
                      className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setFormData(area);
                      }}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => handleTabChange('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => handleTabChange('team')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'team'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserGroupIcon className="h-4 w-4 mr-1" />
              Team ({members.length})
            </button>
            <button
              onClick={() => handleTabChange('tasks')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'tasks'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ClipboardDocumentListIcon className="h-4 w-4 mr-1" />
              Tasks ({tasks.length})
            </button>
            <button
              onClick={() => handleTabChange('okrs')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'okrs'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              OKRs ({okrs.length})
            </button>
            <button
              onClick={() => handleTabChange('research')}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'research'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BeakerIcon className="h-4 w-4 mr-1" />
              Research
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Basic Information
                </h3>
                {editMode ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Owner
                        </label>
                        <select
                          value={formData.owner_id || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              owner_id: e.target.value,
                            })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        >
                          <option value="">No owner</option>
                          {orgMembers.map((member) => (
                            <option key={member.user_id} value={member.user_id}>
                              {member.user_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Maturity Level
                        </label>
                        <select
                          value={formData.maturity_level || 'developing'}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              maturity_level: e.target.value as any,
                            })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        >
                          {MATURITY_LEVELS.map((level) => (
                            <option key={level.value} value={level.value}>
                              {level.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Color
                        </label>
                        <input
                          type="color"
                          value={formData.color || '#3B82F6'}
                          onChange={(e) =>
                            setFormData({ ...formData, color: e.target.value })
                          }
                          className="mt-1 block w-full h-10 border-gray-300 rounded-md shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Status
                        </label>
                        <select
                          value={formData.is_active ? 'true' : 'false'}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              is_active: e.target.value === 'true',
                            })
                          }
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">
                        Description:
                      </span>
                      <p className="text-gray-900">
                        {area.description || 'No description provided'}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-500">Owner:</span>
                        <p className="text-gray-900">
                          {area.owner_name || 'No owner assigned'}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Maturity:</span>
                        <p className="text-gray-900">
                          {getMaturityBadge(area.maturity_level)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* KPIs */}
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Key Performance Indicators
                  </h3>
                  {editMode && (
                    <button
                      onClick={() => setShowAddKPI(true)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Add KPI
                    </button>
                  )}
                </div>

                {/* Add KPI Form */}
                {editMode && showAddKPI && (
                  <div className="border rounded-md p-4 mb-4 bg-gray-50">
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="KPI Name *"
                        value={newKPI.name}
                        onChange={(e) =>
                          setNewKPI({ ...newKPI, name: e.target.value })
                        }
                        className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Unit (e.g., %, count)"
                        value={newKPI.unit}
                        onChange={(e) =>
                          setNewKPI({ ...newKPI, unit: e.target.value })
                        }
                        className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Target Value"
                        value={newKPI.target}
                        onChange={(e) =>
                          setNewKPI({ ...newKPI, target: e.target.value })
                        }
                        className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Current Value"
                        value={newKPI.current}
                        onChange={(e) =>
                          setNewKPI({ ...newKPI, current: e.target.value })
                        }
                        className="block w-full border-gray-300 rounded-md shadow-sm text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Description"
                        value={newKPI.description}
                        onChange={(e) =>
                          setNewKPI({ ...newKPI, description: e.target.value })
                        }
                        className="block w-full border-gray-300 rounded-md shadow-sm text-sm col-span-2"
                      />
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={() => setShowAddKPI(false)}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddKPI}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {/* KPI List */}
                {(editMode ? formData.kpis : area.kpis)?.length ? (
                  <div className="space-y-3">
                    {(editMode ? formData.kpis : area.kpis)?.map(
                      (kpi, index) => (
                        <div
                          key={index}
                          className="border rounded-lg p-4 bg-gray-50"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">
                                {kpi.name}
                              </h4>
                              {kpi.description && (
                                <p className="text-sm text-gray-500 mt-1">
                                  {kpi.description}
                                </p>
                              )}
                              <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                                {kpi.target && (
                                  <div>
                                    <span className="text-gray-500">
                                      Target:
                                    </span>{' '}
                                    <span className="font-medium">
                                      {kpi.target} {kpi.unit}
                                    </span>
                                  </div>
                                )}
                                {kpi.current && (
                                  <div>
                                    <span className="text-gray-500">
                                      Current:
                                    </span>{' '}
                                    <span className="font-medium">
                                      {kpi.current} {kpi.unit}
                                    </span>
                                  </div>
                                )}
                                {kpi.frequency && (
                                  <div>
                                    <span className="text-gray-500">
                                      Frequency:
                                    </span>{' '}
                                    <span className="font-medium">
                                      {kpi.frequency}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {editMode && (
                              <button
                                onClick={() => handleRemoveKPI(index)}
                                className="ml-3 text-red-600 hover:text-red-800"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">No KPIs defined</p>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Statistics */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Statistics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Team Members</span>
                    <span className="text-sm font-medium text-gray-900">
                      {members.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Active Tasks</span>
                    <span className="text-sm font-medium text-gray-900">
                      {tasks.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">OKRs</span>
                    <span className="text-sm font-medium text-gray-900">
                      {okrs.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">
                      Research Notes
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {area.research_count || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Metadata
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-500">Created</span>
                    <p className="text-sm text-gray-900">
                      {new Date(area.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Last Updated</span>
                    <p className="text-sm text-gray-900">
                      {new Date(area.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Display Order</span>
                    <p className="text-sm text-gray-900">
                      {area.display_order}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Tab */}
        {activeTab === 'team' && (
          <div className="bg-white shadow rounded-lg">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Team Members
                </h3>
                {userCanEdit && (
                  <button
                    onClick={() => setShowAddMember(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <UserPlusIcon className="h-4 w-4 mr-2" />
                    Add Member
                  </button>
                )}
              </div>
            </div>

            {/* Add Member Form */}
            {showAddMember && (
              <div className="p-6 bg-gray-50 border-b border-gray-200">
                <div className="flex gap-3">
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="flex-1 border-gray-300 rounded-md shadow-sm"
                  >
                    <option value="">Select a user...</option>
                    {getAvailableMembers().map((member) => (
                      <option key={member.user_id} value={member.user_id}>
                        {member.user_name} ({member.user_email})
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="border-gray-300 rounded-md shadow-sm"
                  >
                    {MEMBER_ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAddMember}
                    disabled={!selectedUserId}
                    className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setShowAddMember(false);
                      setSelectedUserId('');
                      setSelectedRole('member');
                    }}
                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Members List */}
            <div className="divide-y divide-gray-200">
              {members.length > 0 ? (
                members.map((member) => (
                  <div key={member.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-gray-600 font-medium">
                            {member.user_name?.[0] ||
                              member.user_email?.[0] ||
                              '?'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.user_name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {member.user_email}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {userCanEdit ? (
                          <select
                            value={member.role}
                            onChange={(e) =>
                              handleUpdateMemberRole(
                                member.user_id,
                                e.target.value,
                              )
                            }
                            className="border-gray-300 rounded-md shadow-sm text-sm"
                          >
                            {MEMBER_ROLES.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            {MEMBER_ROLES.find((r) => r.value === member.role)
                              ?.label || member.role}
                          </span>
                        )}
                        {userCanEdit && (
                          <button
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      Joined {new Date(member.joined_at).toLocaleDateString()}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-gray-500">
                  No team members assigned to this area
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Tasks for {area.name}
              </h3>
              <button
                onClick={() =>
                  setTaskView(taskView === 'kanban' ? 'list' : 'kanban')
                }
                className="rounded-md border border-gray-200 p-2 text-gray-600 transition-colors hover:bg-gray-100"
                title={
                  taskView === 'kanban'
                    ? 'Switch to List View'
                    : 'Switch to Kanban View'
                }
              >
                {taskView === 'kanban' ? (
                  <ListBulletIcon className="h-4 w-4" />
                ) : (
                  <Squares2X2Icon className="h-4 w-4" />
                )}
              </button>
            </div>

            {taskView === 'list' ? (
              <TaskListView
                tasks={tasks}
                canEdit={userCanEdit}
                organizationId={organization?.id || ''}
                onTaskUpdate={(
                  taskId: string,
                  updates: Partial<MasterPlanTask>,
                ) => {
                  setTasks(
                    tasks.map((t) =>
                      t.id === taskId ? { ...t, ...updates } : t,
                    ),
                  );
                }}
                onTaskDelete={(taskId: string) => {
                  setTasks(tasks.filter((t) => t.id !== taskId));
                }}
                onTaskCreate={async (newTask: Partial<MasterPlanTask>) => {
                  // Create task with area_id
                  if (organization && id) {
                    try {
                      const createdTask = await masterPlanTaskService.create(
                        organization.id,
                        {
                          ...newTask,
                          area_ids: [id],
                        } as any,
                      );
                      setTasks([...tasks, createdTask]);
                    } catch (error) {
                      console.error('Error creating task:', error);
                    }
                  }
                }}
              />
            ) : (
              <TaskKanbanView
                tasks={tasks}
                canEdit={userCanEdit}
                organizationId={organization?.id || ''}
                onTaskUpdate={(
                  taskId: string,
                  updates: Partial<MasterPlanTask>,
                ) => {
                  setTasks(
                    tasks.map((t) =>
                      t.id === taskId ? { ...t, ...updates } : t,
                    ),
                  );
                }}
                onTaskDelete={(taskId: string) => {
                  setTasks(tasks.filter((t) => t.id !== taskId));
                }}
                onTaskCreate={async (newTask: Partial<MasterPlanTask>) => {
                  // Create task with area_id
                  if (organization && id) {
                    try {
                      const createdTask = await masterPlanTaskService.create(
                        organization.id,
                        {
                          ...newTask,
                          area_ids: [id],
                        } as any,
                      );
                      setTasks([...tasks, createdTask]);
                    } catch (error) {
                      console.error('Error creating task:', error);
                    }
                  }
                }}
              />
            )}

            {tasks.length === 0 && (
              <div className="text-center py-12 bg-white rounded-lg">
                <p className="text-gray-500">No tasks assigned to this area</p>
              </div>
            )}
          </div>
        )}

        {/* OKRs Tab */}
        {activeTab === 'okrs' && (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                OKRs for {area.name}
              </h3>
              {userCanEdit && (
                <button
                  onClick={() => navigate(`/okrs?area=${id}`)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add OKR
                </button>
              )}
            </div>

            <div className="space-y-4">
              {okrs.length > 0 ? (
                okrs.map((okr) => (
                  <div
                    key={okr.id}
                    className="bg-white shadow rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/okrs/${okr.id}`)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-gray-900">
                          {okr.objective}
                        </h4>
                        {okr.description && (
                          <p className="text-gray-500 mt-1">
                            {okr.description}
                          </p>
                        )}
                        <div className="mt-3 flex items-center gap-4 text-sm">
                          {(okr as any).owner_name && (
                            <span className="text-gray-500">
                              Owner:{' '}
                              <span className="font-medium">
                                {(okr as any).owner_name}
                              </span>
                            </span>
                          )}
                          {okr.key_results && okr.key_results.length > 0 && (
                            <span className="text-gray-500">
                              {okr.key_results.length} Key Result
                              {okr.key_results.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="ml-4 flex flex-col items-end">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            okr.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : okr.status === 'active'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {okr.status}
                        </span>
                        {(okr as any).progress !== undefined && (
                          <div className="mt-2">
                            <div className="text-sm text-gray-500">
                              Progress
                            </div>
                            <div className="text-lg font-semibold">
                              {(okr as any).progress}%
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 bg-white rounded-lg">
                  <p className="text-gray-500">No OKRs assigned to this area</p>
                  {userCanEdit && (
                    <button
                      onClick={() => navigate(`/okrs?area=${id}`)}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Create First OKR
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Research Tab */}
        {activeTab === 'research' && area && organization && (
          <ResearchTab
            entityId={area.id}
            entityType={'area' as ResearchEntityType}
            entityName={area.name}
            organizationId={organization.id}
            canEdit={userCanEdit}
          />
        )}
      </div>
    </div>
  );
}
